import React from 'react';
import { useNavigate } from 'react-router-dom';
import { LockKey, Columns, ChatCircleText, Envelope, Handshake } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import { Frame } from '../components/shell/AppShell';
import InvitationNotice from '../components/sworn/InvitationNotice';
import { navFor } from '../lib/navigation';
import roomArt from '../assets/bg/member-room.webp';

/**
 * Room: where a member arrives. It stores nothing and shows nobody -- it is a
 * quiet way into what already exists.
 *
 * The four doors are real buttons in a responsive block, not hotspots pinned
 * to things painted in the picture: art crops differently on every screen,
 * and a hotspot that drifts off its lamp is a button nobody can find.
 *
 * The Sworn is a fifth door only for somebody in it. For anybody else there
 * is no gap, no locked tile, nothing -- the grid simply has four.
 *
 * PILOT (2.0 interactive Room, step B): the painting on the left wall also
 * opens the Private Vault. Decision 4's "no hotspots on painted objects" is
 * deliberately changed for it -- see SceneObjects for why it cannot drift off
 * the painting. The Private Vault door below stays until the pilot is
 * reviewed; both go to the same /vault, which is untouched.
 */

// The artwork is 941 x 1672. Focus 0.4 (Founder's choice from the
// composition audit): the only point where both the painting and the seal on
// the right wall stay usable on tall phones.
const ROOM_ART_ASPECT = 941 / 1672;
const ROOM_FOCUS_X = 0.4;

// Measured on the master, as fractions of its width and height: the gilt
// frame of the painting, not the picture lamp above it.
const PAINTING = [0.040, 0.280, 0.234, 0.448];
const DOORS = [
  { key: 'vault', label: 'Private Vault', icon: LockKey, to: '/vault' },
  { key: 'commons', label: 'Enter the Commons', icon: Columns, to: '/commons' },
  { key: 'support', label: 'Private Support', icon: ChatCircleText, to: '/dashboard' },
  // Opens the member's own Support thread in Capsule compose mode. Support
  // becomes the active destination; `from` lets Cancel come back here.
  { key: 'capsule', label: 'Secure Capsule', icon: Envelope, to: '/dashboard', state: { capsule: true, from: '/room' } },
];

const Door = ({ label, icon: Icon, onClick, testid }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testid}
    className={[
      'flex min-h-[64px] items-center gap-3 rounded-xl border border-border px-4 py-3 text-left',
      'bg-card/80 backdrop-blur-sm transition-colors hover:bg-card hover:border-primary/40',
      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
    ].join(' ')}
  >
    <Icon className="w-5 h-5 shrink-0" style={{ color: 'hsl(var(--gold))' }} aria-hidden="true" />
    <span className="text-sm font-medium leading-snug text-foreground">{label}</span>
  </button>
);

const Room = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const extras = navFor(user).roomExtras;
  const no = user?.member_no != null ? String(user.member_no).padStart(3, '0') : null;

  const scene = [
    {
      key: 'vault',
      label: 'Private Vault',
      hint: 'Private Vault',
      box: PAINTING,
      // PROTOTYPE: the painting swings open on its left edge before the
      // Vault opens (LidReveal). Hinge side is not settled; see the lid
      // composition notes. Reduced motion skips it entirely.
      reveal: { hinge: 'left' },
      onActivate: () => navigate('/vault'),
    },
  ];

  return (
    <Frame
      user={user}
      background={roomArt}
      activeKey="room"
      focusX={ROOM_FOCUS_X}
      artAspect={ROOM_ART_ASPECT}
      scene={scene}
    >
      <div
        className="flex flex-col px-4"
        // One screen less the navigation, so the doors sit at the foot of it.
        style={{ minHeight: 'calc(100dvh - 64px - env(safe-area-inset-bottom) - env(safe-area-inset-top))' }}
        data-testid="room"
      >
        <header className="pt-5 text-center">
          <h1
            className="font-heading text-2xl tracking-[0.22em] uppercase"
            style={{ color: 'hsl(var(--gold-pale))' }}
          >
            Arcanum
          </h1>
          {/* Discreet: whose room this is, and nothing anyone else could use. */}
          <p className="mt-1 text-[11px] uppercase tracking-[0.18em] text-muted-foreground" data-testid="room-context">
            Private chamber{no ? ` · Member No. ${no}` : ''}
          </p>
        </header>

        {/* The scene stays visible between the name and the doors. */}
        <div className="flex-1 min-h-[24px]" />

        {/* Above the scene layer, so a painted object can never sit on top
            of a real control. */}
        <div className="relative z-20 mx-auto w-full max-w-md space-y-3 pb-4">
          <InvitationNotice variant="card" />
          <div className="grid grid-cols-2 gap-3" data-testid="room-doors">
            {DOORS.map((d) => (
              <Door
                key={d.key}
                label={d.label}
                icon={d.icon}
                testid={`room-${d.key}`}
                onClick={() => navigate(d.to, d.state ? { state: d.state } : undefined)}
              />
            ))}
          </div>
          {extras.map((x) => (
            <div key={x.key} className="grid grid-cols-1">
              <Door
                label={x.label}
                icon={Handshake}
                testid={`room-${x.key}`}
                onClick={() => navigate(x.to)}
              />
            </div>
          ))}
        </div>
      </div>
    </Frame>
  );
};

export default Room;
